<script setup lang="ts">
import { maxLength, required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { track } from '../../utils/tracking';
import { MAX_NAME_LENGTH } from '../../utils/constants/validationLimits';
import FormElement from '../molecules/form/FormElement.vue';
import FormEmail from './form/FormEmail.vue';
import FormInput from '../atoms/form/FormInput.vue';
import FormLabel from '../atoms/form/FormLabel.vue';
import FormToolTip from '../atoms/form/FormToolTip.vue';
import FormMessage from '../atoms/form/FormMessage.vue';
import salutationTypes from '../../data/salutation-types.json';
import phoneCountryCodes from '../../data/phone-country-codes.json';
import FormSection from './form/FormSection.vue';
import { useApplicationStore } from '../../stores/application-store';
import ComboBox from '../headless/ComboBox.vue';
import { phoneNumber as phoneNumberValidator } from '../../utils/validators';

const application = useApplicationStore();
const {
    firstname,
    lastname,
    salutation,
    phoneNumber,
    countryCode,
    nationalNumber,
    extractedFirstname,
    extractedLastname,
    extractedPhoneNumber,
} = storeToRefs(application);

const nationalNumberRef = ref(null);

const validations = {
    firstname: {
        required,
        maxLength: maxLength(MAX_NAME_LENGTH),
    },
    lastname: {
        required,
        maxLength: maxLength(MAX_NAME_LENGTH),
    },
    salutation: {
        required,
    },
    phoneNumber: {
        required,
        format: phoneNumberValidator,
    },
};

const v$ = useVuelidate(validations, { firstname, lastname, salutation, phoneNumber }, { $registerAs: 'personalData' });

/* CV Extraction */
const isFirstnameChangeTracked = ref(false);
const isLastnameChangeTracked = ref(false);
const isPhoneNumberChangeTracked = ref(false);

// Track changed fields after extraction only once
const trackFirstnameChange = computed(() => !isFirstnameChangeTracked.value &&
    extractedFirstname.value &&
    extractedFirstname.value !== firstname.value);

const trackLastnameChange = computed(() => !isLastnameChangeTracked.value &&
    extractedLastname.value &&
    extractedLastname.value !== lastname.value);

const trackPhoneNumberChange = computed(() => !isPhoneNumberChangeTracked.value &&
    extractedPhoneNumber.value &&
    extractedPhoneNumber.value !== phoneNumber.value);

function onFirstNameBlur() {
    v$.value.firstname.$touch();
    application.updateCvRenderData();

    if (trackFirstnameChange.value) {
        track({
            element: 'AP_F: field changed after extraction',
            elementDetail: 'first name',
        });

        isFirstnameChangeTracked.value = true;
    }
}

function onLastNameBlur() {
    v$.value.lastname.$touch();
    application.updateCvRenderData();

    if (trackLastnameChange.value) {
        track({
            element: 'AP_F: field changed after extraction',
            elementDetail: 'last name',
        });

        isLastnameChangeTracked.value = true;
    }
}

function onPhoneNumberBlur() {
    v$.value.phoneNumber.$touch();
    if (!v$.value.phoneNumber.$error) application.updateCvRenderData();

    if (trackPhoneNumberChange.value) {
        track({
            element: 'AP_F: field changed after extraction',
            elementDetail: 'phone',
        });

        isPhoneNumberChangeTracked.value = true;
    }

    // Remove all whitespaces
    nationalNumber.value = nationalNumber.value.replace(/\s/g, '');
}

/* Salutation model */
const salutationModel = computed({
    get: () => salutationTypes.find(({ value }) => salutation.value === value) || null,
    set: (newModelValue) => { salutation.value = newModelValue.value; },
});

function focusNationalNumberField() {
    nationalNumberRef.value?.focusInput();
}

/* CountryCode model */
const countryCodeModel = computed({
    get: () => phoneCountryCodes.find(({ prefix }) => countryCode.value === prefix) || null,
    set: (newModelValue) => { countryCode.value = newModelValue.prefix; focusNationalNumberField(); },
});

</script>

<template>
    <FormSection
        data-qa="personal data"
    >
        <FormElement>
            <template #start>
                <div class="flex justify-between">
                    <FormLabel>
                        Anrede
                    </FormLabel>
                    <FormToolTip class="text-right flex-grow">
                        <span class="underline decoration-dotted">
                            Warum ist das notwendig?
                        </span>
                        <template #description>
                            Mit einer Anrede kann dich das Unternehmen beim Erstkontakt persönlich ansprechen.
                        </template>
                    </FormToolTip>
                </div>
            </template>
            <ComboBox
                id="salutation"
                v-model="salutationModel"
                :options="salutationTypes"
                data-qa="salutation"
            />
            <template #end>
                <FormMessage
                    v-if="v$.salutation.$error"
                    type="error"
                    data-qa="salutation form message"
                >
                    <template v-if="v$.salutation.required.$invalid">
                        Bitte wähle eine Anrede aus.
                    </template>
                </FormMessage>
            </template>
        </FormElement>
        <div class="m:flex">
            <FormElement class="w-full pt-m">
                <template #start>
                    <FormLabel>
                        Vorname
                    </FormLabel>
                </template>
                <FormInput
                    v-model.trim="firstname"
                    type="text"
                    placeholder="Vorname"
                    :status="v$.firstname.$error ? 'error' : ''"
                    :show-sparkle-icon="extractedFirstname !== ''"
                    data-qa="firstname"
                    data-gtm-element="AP_F: Personal Data"
                    data-gtm-element-detail="First-Name"
                    @update:model-value="v$.firstname.$touch()"
                    @blur="onFirstNameBlur"
                />
                <template #end>
                    <FormMessage
                        v-if="v$.firstname.$error"
                        type="error"
                        data-qa="firstname form message"
                    >
                        <template v-if="v$.firstname.required.$invalid">
                            Bitte fülle das Feld Vorname aus.
                        </template>
                        <template v-else-if="v$.firstname.maxLength.$invalid">
                            Bitte fülle das Feld Vorname aus, mit max. {{ MAX_NAME_LENGTH }} Zeichen.
                        </template>
                    </FormMessage>
                </template>
            </FormElement>

            <FormElement class="w-full pt-m m:pl-m">
                <template #start>
                    <FormLabel>
                        Nachname
                    </FormLabel>
                </template>
                <FormInput
                    v-model.trim="lastname"
                    type="text"
                    placeholder="Nachname"
                    :status="v$.lastname.$error ? 'error' : ''"
                    :show-sparkle-icon="extractedLastname !== ''"
                    data-qa="lastname"
                    data-gtm-element="AP_F: Personal Data"
                    data-gtm-element-detail="Last-Name"
                    @update:model-value="v$.lastname.$touch()"
                    @blur="onLastNameBlur"
                />
                <template #end>
                    <FormMessage
                        v-if="v$.lastname.$error"
                        type="error"
                        data-qa="lastname form message"
                    >
                        <template v-if="v$.lastname.required.$invalid">
                            Bitte fülle das Feld Nachname aus.
                        </template>
                        <template v-else-if="v$.lastname.maxLength.$invalid">
                            Bitte fülle das Feld Nachname aus, mit max. {{ MAX_NAME_LENGTH }} Zeichen.
                        </template>
                    </FormMessage>
                </template>
            </FormElement>
        </div>
        <div class="m:flex">
            <FormEmail class="w-full pt-m"/>
            <FormElement class="w-full pt-m m:pl-m">
                <template #start>
                    <FormLabel>
                        Telefonnummer
                    </FormLabel>
                </template>
                <div class="flex">
                    <div class="pr-m">
                        <ComboBox
                            id="countryCode"
                            v-model="countryCodeModel"
                            class="w-[89px]"
                            :options="phoneCountryCodes"
                            :label="countryCodeModel?.prefix"
                            data-qa="countryCode"
                            dynamic-popover-width
                        />
                    </div>
                    <div class="flex-1">
                        <FormInput
                            ref="nationalNumberRef"
                            v-model.trim="nationalNumber"
                            type="text"
                            placeholder="123456789"
                            :status="v$.phoneNumber.$error ? 'error' : undefined"
                            :show-sparkle-icon="extractedPhoneNumber !== ''"
                            data-qa="nationalNumber"
                            @update:model-value="v$.phoneNumber.$touch()"
                            @blur="onPhoneNumberBlur"
                        />
                    </div>
                </div>
                <template #end>
                    <FormMessage
                        v-if="v$.phoneNumber.$error"
                        type="error"
                        data-qa="phoneNumber form message"
                    >
                        <template v-if="v$.phoneNumber.required.$invalid">
                            Bitte gib eine Telefonnummer an.
                        </template>
                        <template v-else-if="v$.phoneNumber.format.$invalid">
                            Bitte gib eine korrekte Telefonnummer ein.
                        </template>
                    </FormMessage>
                </template>
            </FormElement>
        </div>
    </FormSection>
</template>

<style lang="scss">
@use '~@karriereat/global-styles/scss/settings/spacing';
@use '~@karriereat/global-styles/scss/settings/breakpoint';

.o-personalData {
    $root: &;

    &__nameWrap {
        display: flex;
        flex-wrap: wrap;
        margin-left: -(spacing.$k-spacing--s);
    }

    &__nameWrapField {
        padding-top: spacing.$k-spacing--m;
        padding-left: spacing.$k-spacing--s;
        width: 100%;

        @media (min-width: breakpoint.$k-breakpoint--m) {
            width: 50%;
        }
    }

    &__salutationWrap {
        padding-top: spacing.$k-spacing--m;
    }
}
</style>
